import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getCurrentUserId, getAccessToken } from "../../awsAuth";

import FeedActions from "./FeedActions";
import ListingDetails from "../CreateListing/ListingDetails";
import ListingCard from "./Listing/ListingCard";
import PostCard from "./Post/PostCard";
import ShowcaseAnnouncementCard from "./ShowcaseAnnouncementCard";
import ShowcasesRecommendRow from "./ShowcasesRecommendRow";
import WantedRecommendRow from "./WantedRecommendRow";
import PostsRecommendRow from "./PostsRecommendRow";
import "./Feed.scss";
import AdSlot from "../Ads/AdSlot";
import { useSubscription } from "../../contexts/SubscriptionContext";
import FeedbackBanner from "../Feedback/FeedbackBanner";
// eslint-disable-next-line no-unused-vars -- kept for the commented-out render below, re-enable by uncommenting both
import TelegramBaner from "./TelegramBaner";
import CollectionsRecommendRow from "./CollectionsRecommendRow";
import CollectionModal from "./CollectionModal";
import { buildDynamicCollections } from "./utils/buildDynamicCollections";
import {
  trackOpenCollection,
  trackOpenListingDetails,
  trackSaveListing,
  trackLikeListing,
} from "../../utils/trackActivity";
import { categoryImages } from "../../utils/categoriesMap";

import { HARD_TTL, cacheGetStale, cacheSet } from "../../utils/swrcache";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

const FEED_FILTER_LS_KEY = "feed:filter";

const ENABLE_LISTING_PREFETCH =
  process.env.REACT_APP_ENABLE_LISTING_PREFETCH === "1";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const getAuthHeaders = async () => {
  const token = await getAccessToken();

  return token
    ? {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }
    : {
        "Content-Type": "application/json",
      };
};

/* ===== interaction cache keys (SWRCACHE) ===== */
const FEED_INTERACTIONS_TTL = Math.max(
  30 * 1000,
  Math.min(Number(HARD_TTL || 0) || 60 * 1000, 5 * 60 * 1000)
);

const feedInteractionsKey = (userId) =>
  userId ? `feed:interactions:v1:${String(userId)}` : null;

const formatTimeAgo = (ts) => {
  const d = typeof ts === "number" ? new Date(ts) : new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);

  if (diffMin < 1) return "щойно";
  if (diffMin < 60) return `${diffMin} хв тому`;
  if (diffH < 24) return `${diffH} год тому`;
  if (diffD === 1) return "вчора";

  const months = [
    "січня",
    "лютого",
    "березня",
    "квітня",
    "травня",
    "червня",
    "липня",
    "серпня",
    "вересня",
    "жовтня",
    "листопада",
    "грудня",
  ];

  return `${d.getDate()} ${months[d.getMonth()]}`;
};

const ImpressionOnce = ({
  threshold = 0.6,
  onSeen,
  children,
  className = "",
  style = {},
}) => {
  const ref = useRef(null);
  const seenRef = useRef(false);

  useEffect(() => {
    if (!ref.current || !onSeen) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const r =
            typeof e.intersectionRatio === "number"
              ? e.intersectionRatio
              : 0;

          if (e.isIntersecting && r >= threshold && !seenRef.current) {
            seenRef.current = true;
            onSeen();
          }
        });
      },
      { threshold }
    );

    obs.observe(ref.current);

    return () => {
      try {
        obs.unobserve(ref.current);
      } catch {}

      obs.disconnect();
    };
  }, [threshold, onSeen]);

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
};

const FeedSkeleton = () => {
  const placeholders = new Array(8).fill(0);

  return (
    <>
      <div className="feed-topbar skeleton">
        <div className="feed-topbar__title skeleton__line" />

        <div className="feed-tabs">
          <div className="feed-tab skeleton__pill" />
          <div className="feed-tab skeleton__pill" />
          <div className="feed-tab skeleton__pill" />
          <div className="feed-tab skeleton__pill" />
        </div>
      </div>

      <div className="feed">
        {placeholders.map((_, idx) => (
          <div className="feed__grid-item feed__grid-item--listing" key={idx}>
            <div className="feed__card feed__card--listing">
              <div className="sk-product-card">
                <div className="sk-product-image skeleton__block" />
                <div className="skeleton__line short" />
                <div className="skeleton__line tiny" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

function hardUnlockScroll() {
  if (typeof document === "undefined") return;

  const html = document.documentElement;
  const body = document.body;

  html.classList.remove("cv-modal-open");
  body.classList.remove("cv-modal-open");

  html.style.overflow = "";
  body.style.overflow = "";

  html.style.position = "";
  body.style.position = "";

  html.style.top = "";
  body.style.top = "";

  html.style.left = "";
  body.style.left = "";

  html.style.right = "";
  body.style.right = "";

  html.style.width = "";
  body.style.width = "";

  html.style.height = "";
  body.style.height = "";

  html.style.touchAction = "";
  body.style.touchAction = "";

  html.style.overscrollBehavior = "";
  body.style.overscrollBehavior = "";
}

const ScrollLock = (() => {
  let count = 0;
  let savedY = 0;

  const addClass = () => {
    document.documentElement.classList.add("cv-modal-open");
    document.body.classList.add("cv-modal-open");
  };

  const removeClass = () => {
    document.documentElement.classList.remove("cv-modal-open");
    document.body.classList.remove("cv-modal-open");
  };

  return {
    lock: () => {
      if (count === 0) {
        savedY = window.scrollY || document.documentElement.scrollTop || 0;

        addClass();

        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
      }

      count += 1;
    },

    unlock: () => {
      count = Math.max(0, count - 1);

      if (count === 0) {
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";

        removeClass();

        setTimeout(() => {
          try {
            window.scrollTo({ top: savedY, behavior: "instant" });
          } catch {
            window.scrollTo(0, savedY);
          }
        }, 0);
      }
    },

    forceReset: () => {
      count = 0;

      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";

      removeClass();
    },
  };
})();

const BATCH_SIZE = 12;
const FEED_PAGE_LIMIT = 20;

const nowSec = () => Math.floor(Date.now() / 1000);
const BOOST_RANK_BONUS = 10_000_000_000_000;
const boostStatusCache = new Map();

const fetchBoostStatusSafe = async () => null;

const mapWithConcurrency = async (arr, limit, fn) => {
  const out = new Array(arr.length);
  let i = 0;

  const workers = new Array(Math.min(limit, arr.length))
    .fill(0)
    .map(async () => {
      while (i < arr.length) {
        const idx = i++;
        out[idx] = await fn(arr[idx], idx);
      }
    });

  await Promise.all(workers);

  return out;
};

const attachBoostToListings = async (listings) => {
  return Array.isArray(listings)
    ? listings.map((l) => ({
        ...l,
        boostActive: !!l?.boostActive,
        boostEndsAtSec: l?.boostEndsAtSec || null,
        boostPlan: l?.boostPlan || null,
        boostStatus: l?.boostStatus || null,
        isSponsored: !!(l?.isSponsored || l?.sponsored || l?.boostActive),
        sponsored: !!(l?.sponsored || l?.isSponsored || l?.boostActive),
        rank: l?.rank || l?.createdAt || 0,
      }))
    : [];
};

function mixAnnouncementsIntoFeed(baseItems, announcements, everyN = 6) {
  const base = Array.isArray(baseItems) ? baseItems : [];
  const ann = Array.isArray(announcements) ? announcements : [];

  if (!ann.length) return base;

  const out = [];
  let ai = 0;

  for (let i = 0; i < base.length; i++) {
    out.push(base[i]);

    if ((i + 1) % everyN === 0 && ai < ann.length) {
      out.push({
        ...ann[ai],
        type: "showcase_announcement",
        annType: ann[ai]?.type || "other",
        _annId: ann[ai]?.id ? String(ann[ai].id) : `ann#${ai}`,
      });

      ai++;
    }
  }

  while (ai < ann.length) {
    out.push({
      ...ann[ai],
      type: "showcase_announcement",
      annType: ann[ai]?.type || "other",
      _annId: ann[ai]?.id ? String(ann[ai].id) : `ann#${ai}`,
    });

    ai++;
  }

  return out;
}

function spreadSameAuthorListings(feedItems = []) {
  const arr = Array.isArray(feedItems) ? [...feedItems] : [];

  const isListing = (item) => item?.cardType === "listing";
  const getAuthorId = (item) => String(item?.userId || item?.authorId || "");

  for (let i = 1; i < arr.length; i++) {
    const prev = arr[i - 1];
    const curr = arr[i];

    if (!isListing(prev) || !isListing(curr)) continue;

    const prevAuthorId = getAuthorId(prev);
    const currAuthorId = getAuthorId(curr);

    if (!prevAuthorId || !currAuthorId) continue;
    if (prevAuthorId !== currAuthorId) continue;

    let swapIndex = -1;

    for (let j = i + 1; j < arr.length; j++) {
      const candidate = arr[j];

      if (!isListing(candidate)) {
        swapIndex = j;
        break;
      }

      const candidateAuthorId = getAuthorId(candidate);

      if (candidateAuthorId && candidateAuthorId !== prevAuthorId) {
        swapIndex = j;
        break;
      }
    }

    if (swapIndex !== -1) {
      [arr[i], arr[swapIndex]] = [arr[swapIndex], arr[i]];
    }
  }

  return arr;
}

const Feed = ({ categoryName = "", shoppingOnly = false, audience = "", department = "" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [savedItems, setSavedItems] = useState([]);
  const [likedItems, setLikedItems] = useState([]);
  const [likesCount, setLikesCount] = useState({});
  const [savedCounts, setSavedCounts] = useState({});
  const [commentsCounts, setCommentsCounts] = useState({});
  const [modalListingId, setModalListingId] = useState(null);
  const navigate = useNavigate();
  const [removingIds, setRemovingIds] = useState([]);

  // entitlements.externalAdsEnabled is the canSeeAds entitlement: true for
  // Free, false for active Plus/Business (and back to true once a paid plan
  // expires — server-computed, see server/utils/entitlements.js). When
  // false, <AdSlot> below must not mount at all, not just render hidden.
  const { entitlements: subscriptionEntitlements } = useSubscription();
  const canSeeAds = subscriptionEntitlements?.externalAdsEnabled !== false;

    const [gridColumns, setGridColumns] = useState(() => {
    if (typeof window === "undefined") return 2;

    const w = window.innerWidth;

    if (w >= 1120) return 4;
    if (w >= 760) return 3;
    return 2;
  });

  const deletedIdsRef = useRef(new Set());

  const [showcaseRecs, setShowcaseRecs] = useState([]);
  const [showShowcaseRecs, setShowShowcaseRecs] = useState(false);

  const [wantedRecs, setWantedRecs] = useState([]);
  const [showWantedRecs, setShowWantedRecs] = useState(false);
  
  const [postRecs, setPostRecs] = useState([]);
  const [showPostRecs, setShowPostRecs] = useState(false);

  const [activeCollection, setActiveCollection] = useState(null);
  const collectionSeed = useMemo(() => Date.now(), []);

  const [filter, setFilter] = useState(() => {
  const v = localStorage.getItem(FEED_FILTER_LS_KEY);

    return v === "listing" || v === "wanted" || v === "post" ? v : "all";
  });

  const clickTimerRef = useRef(null);

  const FEED_CACHE_KEY = categoryName
    ? `feed:${categoryName}`
    : shoppingOnly || audience || department
    ? `feed:shopping:${audience || "-"}:${department || "-"}`
    : "feed:all";
  const allowFeedCache = false;

  const [loadingMore, setLoadingMore] = useState(false);
  const [feedCursor, setFeedCursor] = useState(null);
  const [hasMoreFeed, setHasMoreFeed] = useState(false);

  const listingCacheRef = useRef(new Map());

  const getUnifiedId = useCallback((it) => {
    if (!it) return "";
    if (it.type === "listing") return String(it.listingId || it.id || "");
    if (it.type === "post") return String(it.postId || it.id || "");
    if (it.type === "showcase_announcement")
      return String(it._annId || it.id || "");

    return String(it.id || it.listingId || it.postId || "");
  }, []);

  const getRank = useCallback(
    (x) => Number(x?.rank || x?.createdAt || 0) || 0,
    []
  );

  const purgeDeletedItem = useCallback(
    (type, id) => {
      const sId = String(id);

      deletedIdsRef.current.add(sId);

      setItems((prev) => {
        const next = prev.filter((x) => getUnifiedId(x) !== sId);
        cacheSet(FEED_CACHE_KEY, next);
        return next;
      });

      setRemovingIds((prev) => prev.filter((x) => x !== sId));

      setSavedItems((prev) => prev.filter((x) => String(x) !== sId));
      setLikedItems((prev) => prev.filter((x) => String(x) !== sId));

      setLikesCount((prev) => {
        const copy = { ...(prev || {}) };
        delete copy[sId];
        return copy;
      });

      setSavedCounts((prev) => {
        const copy = { ...(prev || {}) };
        delete copy[sId];
        return copy;
      });

      setCommentsCounts((prev) => {
        const copy = { ...(prev || {}) };
        delete copy[sId];
        return copy;
      });

      if (selectedItem?.id === sId) {
        setSelectedItem(null);
      }

      try {
        listingCacheRef.current.delete(sId);
      } catch {}
    },
    [FEED_CACHE_KEY, allowFeedCache, getUnifiedId, selectedItem]
  );

  const handleHideCard = useCallback(
    (_type, id) => {
      const sId = String(id);

      setRemovingIds((prev) =>
        prev.includes(sId) ? prev : [...prev, sId]
      );

      setTimeout(() => {
        purgeDeletedItem(_type, sId);
      }, 220);
    },
    [purgeDeletedItem]
  );

  const handleDeleteFromFeed = useCallback(
    (type, id) => {
      const sId = String(id);

      setRemovingIds((prev) =>
        prev.includes(sId) ? prev : [...prev, sId]
      );

      setTimeout(() => {
        purgeDeletedItem(type, sId);
      }, 220);
    },
    [purgeDeletedItem]
  );

  const originalURLRef = useRef(
    window.location.pathname + window.location.search
  );

  const pushedForModalRef = useRef(false);

  const openListingModalWithURL = useCallback((listingId, initialThumb) => {
    try {
      const y = window.scrollY || document.documentElement.scrollTop || 0;

      sessionStorage.setItem("feed:scrollY", String(y));

      if (!pushedForModalRef.current) {
        originalURLRef.current =
          window.location.pathname + window.location.search;
      }

      window.history.pushState(
        { __cv_modal: "listing", id: listingId },
        "",
        `/listing/${listingId}`
      );

      pushedForModalRef.current = true;

      ScrollLock.lock();

      if (initialThumb) {
        const prev = listingCacheRef.current.get(listingId) || {};
        listingCacheRef.current.set(listingId, {
          ...prev,
          thumb: initialThumb,
        });
      }

      setModalListingId(listingId);
    } catch {
      ScrollLock.lock();
      setModalListingId(listingId);
    }
  }, []);

  const closeListingModalAndURL = useCallback(() => {
  const y = Number(sessionStorage.getItem("feed:scrollY") || "0") || 0;

  setModalListingId(null);
  pushedForModalRef.current = false;

  try {
    window.history.replaceState(null, "", originalURLRef.current || "/feed");
  } catch {}

  requestAnimationFrame(() => {
    ScrollLock.forceReset();
    hardUnlockScroll();

    setTimeout(() => {
      try {
        window.scrollTo({ top: y, behavior: "instant" });
      } catch {
        window.scrollTo(0, y);
      }
    }, 0);
  });
}, []);

    useEffect(() => {
    const onPop = () => {
      if (modalListingId != null) {
        const y = Number(sessionStorage.getItem("feed:scrollY") || "0") || 0;

        setModalListingId(null);
        pushedForModalRef.current = false;

        ScrollLock.forceReset();
        hardUnlockScroll();

        setTimeout(() => {
          try {
            window.scrollTo({ top: y, behavior: "instant" });
          } catch {
            window.scrollTo(0, y);
          }
        }, 0);
      }
    };

    window.addEventListener("popstate", onPop);

    return () => {
      window.removeEventListener("popstate", onPop);
    };
  }, [modalListingId]);

  useEffect(() => {
    if (modalListingId == null) {
      requestAnimationFrame(() => {
        ScrollLock.forceReset();
        hardUnlockScroll();
      });
    }
  }, [modalListingId]);

  useEffect(() => {
    return () => {
      ScrollLock.forceReset();
      hardUnlockScroll();
    };
  }, []);

    useEffect(() => {
    const updateGridColumns = () => {
      const w = window.innerWidth;

      if (w >= 1120) {
        setGridColumns(4);
      } else if (w >= 760) {
        setGridColumns(3);
      } else {
        setGridColumns(2);
      }
    };

    updateGridColumns();

    window.addEventListener("resize", updateGridColumns);

    return () => {
      window.removeEventListener("resize", updateGridColumns);
    };
  }, []);

  /* ===== interactions cache helpers (likes/saved/likeCounts) ===== */
  const hydrateInteractionsFromCache = useCallback((userId) => {
    try {
      const k = feedInteractionsKey(userId);
      if (!k) return null;

      const cached = cacheGetStale(k, FEED_INTERACTIONS_TTL);
      if (!cached || typeof cached !== "object") return null;

      const saved = Array.isArray(cached.savedItems)
        ? cached.savedItems
        : null;

      const liked = Array.isArray(cached.likedItems)
        ? cached.likedItems
        : null;

      const lcnt =
        cached.likesCount && typeof cached.likesCount === "object"
          ? cached.likesCount
          : null;

      const scnt =
        cached.savedCounts && typeof cached.savedCounts === "object"
          ? cached.savedCounts
          : null;

      const ccnt =
        cached.commentsCounts && typeof cached.commentsCounts === "object"
          ? cached.commentsCounts
          : null;

      if (saved) setSavedItems(saved);
      if (liked) setLikedItems(liked);

      if (lcnt) setLikesCount(lcnt);
      if (scnt) setSavedCounts(scnt);
      if (ccnt) setCommentsCounts(ccnt);

      return cached;
    } catch {
      return null;
    }
  }, []);

  const persistInteractionsToCache = useCallback((userId, payload) => {
    try {
      const k = feedInteractionsKey(userId);
      if (!k) return;

      cacheSet(k, payload);
    } catch {}
  }, []);

  const updateInteractionsCachePartial = useCallback((userId, patch) => {
    try {
      const k = feedInteractionsKey(userId);
      if (!k) return;

      const current = cacheGetStale(k, FEED_INTERACTIONS_TTL) || {};

      const next = {
        ...(current && typeof current === "object" ? current : {}),
        ...(patch || {}),
      };

      cacheSet(k, next);
    } catch {}
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);

        setFeedCursor(null);
        setHasMoreFeed(false);

        if (allowFeedCache) {
          const cached = cacheGetStale(FEED_CACHE_KEY, HARD_TTL);

          if (cached && !cancelled) {
            const safeCached = Array.isArray(cached)
              ? cached.filter(
                  (x) => !deletedIdsRef.current.has(getUnifiedId(x))
                )
              : [];

            setItems(safeCached);
            setLoading(false);
          }
        }

        const userId = getCurrentUserId();

        if (userId) {
          hydrateInteractionsFromCache(userId);
        }

        const feedRes = await axios.get(`${API_BASE}/api/feed/v2`, {
          headers: await getAuthHeaders(),
          params: {
          userId,
          limit: FEED_PAGE_LIMIT,
          ...(categoryName ? { category: categoryName } : {}),
          ...(filter !== "all" ? { contentType: filter } : {}),
          ...(shoppingOnly ? { shoppingOnly: 1 } : {}),
          ...(audience ? { audience } : {}),
          ...(department ? { department } : {}),
        },
        });

        const feedData = feedRes?.data || {};
        const rawItems = Array.isArray(feedData.items) ? feedData.items : [];

        let listings = rawItems
          .filter((x) => x?.type === "listing")
          .map((l) => ({
            ...l,
            cardType: "listing",
            listingType: l.listingType || l.typeRaw || "sale",
          }));

        const posts = rawItems
          .filter((x) => x?.type === "post")
          .map((p) => ({
            ...p,
            type: "post",
          }));

        const seenListingIds = new Set();

        listings = listings.filter((l) => {
          const id = String(l?.listingId || l?.id || "");
          if (!id) return false;
          if (seenListingIds.has(id)) return false;

          seenListingIds.add(id);

          return true;
        });

        listings = await attachBoostToListings(listings);

        const filteredListings = categoryName
          ? listings.filter(
              (x) => x.status !== "archived" && x.category === categoryName
            )
          : listings.filter((x) => x.status !== "archived");

        const filteredPosts = categoryName
          ? posts.filter(
              (x) => x.status !== "archived" && x.category === categoryName
            )
          : posts.filter((x) => x.status !== "archived");

        const merged = [...filteredListings, ...filteredPosts].filter(
          (x) => !deletedIdsRef.current.has(getUnifiedId(x))
        );

        if (cancelled) return;

        setAnnouncements([]);
        setItems(merged);

        if (allowFeedCache) {
          cacheSet(FEED_CACHE_KEY, merged);
        }

        setFeedCursor(feedData.nextCursor || null);
        setHasMoreFeed(!!feedData.hasMore);

        const baseLikesData = {};
        const baseSavedData = {};
        const baseCommentsData = {};

        merged.forEach((item) => {
          const id = getUnifiedId(item);
          if (!id) return;

          baseLikesData[id] =
            Number(item?.stats?.likes ?? item?.likesCount ?? 0) || 0;

          baseSavedData[id] =
            Number(
              item?.stats?.saves ??
                item?.favoritesCount ??
                item?.savesCount ??
                0
            ) || 0;

          baseCommentsData[id] =
            Number(item?.stats?.comments ?? item?.commentsCount ?? 0) || 0;
        });

        setLikesCount(baseLikesData);
        setSavedCounts(baseSavedData);
        setCommentsCounts(baseCommentsData);

        if (userId) {
          const nextSaved = [];
          const likesData = {};
          const savedData = {};
          const commentsData = {};
          const likedByUser = [];

          const cachedInteractions = hydrateInteractionsFromCache(userId) || {};
          const cachedLikesMap = cachedInteractions.likesCount || {};
          const cachedSavedMap = cachedInteractions.savedCounts || {};
          const cachedCommentsMap = cachedInteractions.commentsCounts || {};

          const cachedLikedItems = Array.isArray(cachedInteractions.likedItems)
            ? cachedInteractions.likedItems
            : [];

          const cachedSavedItems = Array.isArray(cachedInteractions.savedItems)
            ? cachedInteractions.savedItems
            : [];

          merged.forEach((item) => {
            const id = getUnifiedId(item);

            const incomingLikes =
              Number(item?.stats?.likes ?? item?.likesCount ?? 0) || 0;

            const incomingSaves =
              Number(
                item?.stats?.saves ??
                  item?.favoritesCount ??
                  item?.savesCount ??
                  0
              ) || 0;

            const incomingComments =
              Number(item?.stats?.comments ?? item?.commentsCount ?? 0) || 0;

            const cachedLikes =
              Number(cachedLikesMap[id] ?? likesCount[id] ?? 0) || 0;

            const cachedSaves =
              Number(cachedSavedMap[id] ?? savedCounts[id] ?? 0) || 0;

            const cachedComments =
              Number(cachedCommentsMap[id] ?? commentsCounts[id] ?? 0) || 0;

            likesData[id] = Math.max(cachedLikes, incomingLikes);
            savedData[id] = Math.max(cachedSaves, incomingSaves);
            commentsData[id] = Math.max(cachedComments, incomingComments);

            if (item?.viewer?.isSaved || cachedSavedItems.includes(id)) {
              nextSaved.push(id);
            }

            if (item?.viewer?.isLiked || cachedLikedItems.includes(id)) {
              likedByUser.push(id);
            }
          });

          setSavedItems(nextSaved);
          setLikedItems(likedByUser);
          setLikesCount(likesData);
          setSavedCounts(savedData);
          setCommentsCounts(commentsData);

          persistInteractionsToCache(userId, {
            savedItems: nextSaved,
            likedItems: likedByUser,
            likesCount: likesData,
            savedCounts: savedData,
            commentsCounts: commentsData,
            savedAt: Date.now(),
          });

          const entities = merged
            .filter((item) => item?.type === "listing" || item?.type === "post")
            .map((item) => ({
              id: getUnifiedId(item),
              type: item?.type === "post" ? "post" : "listing",
            }))
            .filter((x) => x.id);

          if (entities.length) {
            Promise.resolve().then(async () => {
              try {
                const batchRes = await fetch(
                  `${API_BASE}/api/interactions/batch`,
                  {
                    method: "POST",
                    headers: await getAuthHeaders(),
                    body: JSON.stringify({ entities }),
                  }
                );

                if (!batchRes.ok) return;

                const batchData = await batchRes.json().catch(() => []);

                if (!Array.isArray(batchData) || cancelled) return;

                const likesPatch = {};
                const likedPatch = [];
                const savedPatch = [];

                batchData.forEach((row) => {
                  const id = String(row?.id || "");

                  if (!id) return;

                  if (typeof row?.counts?.likes === "number") {
                    likesPatch[id] = Number(row.counts.likes) || 0;
                  }

                  if (row?.viewer?.isLiked === true) likedPatch.push(id);
                  if (row?.viewer?.isSaved === true) savedPatch.push(id);
                });

                setLikesCount((prev) => {
                  const next = { ...prev, ...likesPatch };

                  persistInteractionsToCache(userId, {
                    savedItems: savedPatch.length
                      ? Array.from(new Set([...nextSaved, ...savedPatch]))
                      : nextSaved,

                    likedItems: likedPatch.length
                      ? Array.from(new Set([...likedByUser, ...likedPatch]))
                      : likedByUser,

                    likesCount: next,
                    savedCounts: savedData,
                    commentsCounts: commentsData,
                    savedAt: Date.now(),
                  });

                  return next;
                });

                if (likedPatch.length) {
                  setLikedItems((prev) =>
                    Array.from(new Set([...prev, ...likedPatch]))
                  );
                }

                if (savedPatch.length) {
                  setSavedItems((prev) =>
                    Array.from(new Set([...prev, ...savedPatch]))
                  );
                }
              } catch (e) {
                console.warn("interactions batch hydrate failed", e);
              }
            });
          }
        }
      } catch (err) {
        console.error("Помилка завантаження стрічки", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [
    categoryName,
    shoppingOnly,
    audience,
    department,
    filter,
    FEED_CACHE_KEY,
    allowFeedCache,
    getUnifiedId,
    hydrateInteractionsFromCache,
    persistInteractionsToCache,
    getRank,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (categoryName) {
      setShowcaseRecs([]);
      setShowShowcaseRecs(false);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      try {
        const r = await axios.get(`${API_BASE}/api/showcases/recommended`, {
          params: { limit: 20 },
        });

        const items = Array.isArray(r.data?.items)
          ? r.data.items
          : Array.isArray(r.data)
          ? r.data
          : [];

        if (cancelled) return;

        setShowcaseRecs(items);
        setShowShowcaseRecs(items.length > 0);
      } catch (e) {
        if (!cancelled) {
          setShowcaseRecs([]);
          setShowShowcaseRecs(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [categoryName]);

  useEffect(() => {
  let cancelled = false;

  if (categoryName) {
    setWantedRecs([]);
    setShowWantedRecs(false);

    return () => {
      cancelled = true;
    };
  }

  const run = async () => {
    try {
      const userId = getCurrentUserId();

      const r = await axios.get(`${API_BASE}/api/feed/v2`, {
        params: {
          userId,
          limit: 8,
          contentType: "wanted",
          ...(shoppingOnly ? { shoppingOnly: 1 } : {}),
          ...(audience ? { audience } : {}),
          ...(department ? { department } : {}),
        },
      });

      const items = Array.isArray(r.data?.items) ? r.data.items : [];

      const normalized = items
        .filter((x) => x?.type === "listing" || x?.cardType === "listing")
        .map((x) => ({
          ...x,
          cardType: "listing",
          listingType: "wanted",
          typeRaw: "wanted",
        }))
        .slice(0, 5);

      if (cancelled) return;

      setWantedRecs(normalized);
      setShowWantedRecs(normalized.length > 0);
    } catch (e) {
      if (!cancelled) {
        setWantedRecs([]);
        setShowWantedRecs(false);
      }
    }
  };

  run();

  return () => {
    cancelled = true;
  };
}, [categoryName, shoppingOnly, audience, department]);

useEffect(() => {
  let cancelled = false;

  if (categoryName) {
    setPostRecs([]);
    setShowPostRecs(false);

    return () => {
      cancelled = true;
    };
  }

  const run = async () => {
    try {
      const userId = getCurrentUserId();

      const r = await axios.get(`${API_BASE}/api/feed/v2`, {
        params: {
          userId,
          limit: 10,
          contentType: "post",
          ...(shoppingOnly ? { shoppingOnly: 1 } : {}),
          ...(audience ? { audience } : {}),
          ...(department ? { department } : {}),
        },
      });

      const items = Array.isArray(r.data?.items) ? r.data.items : [];

      const normalized = items
        .filter((x) => x?.type === "post")
        .map((x) => ({
          ...x,
          type: "post",
        }))
        .slice(0, 10);

      if (cancelled) return;

      setPostRecs(normalized);
      setShowPostRecs(normalized.length > 0);
    } catch (e) {
      if (!cancelled) {
        setPostRecs([]);
        setShowPostRecs(false);
      }
    }
  };

  run();

  return () => {
    cancelled = true;
  };
}, [categoryName, shoppingOnly, audience, department]);
  
  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    if (!feedCursor || !hasMoreFeed) return;

    try {
      setLoadingMore(true);

      const userId = getCurrentUserId();

      const feedRes = await axios.get(`${API_BASE}/api/feed/v2`, {
        headers: await getAuthHeaders(),
        params: {
        userId,
        limit: FEED_PAGE_LIMIT,
        cursor: feedCursor,
        ...(categoryName ? { category: categoryName } : {}),
        ...(filter !== "all" ? { contentType: filter } : {}),
        ...(shoppingOnly ? { shoppingOnly: 1 } : {}),
        ...(audience ? { audience } : {}),
        ...(department ? { department } : {}),
      },
      });

      const data = feedRes?.data || {};
      const newItemsRaw = Array.isArray(data.items) ? data.items : [];

      let newItems = newItemsRaw.map((item) => {
        if (item?.type === "listing") {
          return {
            ...item,
            cardType: "listing",
            listingType: item.listingType || item.typeRaw || "sale",
          };
        }

        if (item?.type === "post") {
          return {
            ...item,
            type: "post",
          };
        }

        return item;
      });

      if (newItems.length) {
        newItems = await attachBoostToListings(
          newItems.filter(
            (item) => item?.type === "listing" || item?.cardType === "listing"
          )
        ).then((boostedListings) => {
          const boostedMap = new Map(
            boostedListings.map((x) => [
              String(x?.listingId || x?.id || ""),
              x,
            ])
          );

          return newItems.map((item) => {
            if (item?.type === "listing" || item?.cardType === "listing") {
              const key = String(item?.listingId || item?.id || "");
              return boostedMap.get(key) || item;
            }

            return item;
          });
        });

        setItems((prev) => {
          const merged = [...prev, ...newItems].filter(
            (x) => !deletedIdsRef.current.has(getUnifiedId(x))
          );

          const seen = new Set();

          const deduped = merged.filter((x) => {
            const id = getUnifiedId(x);

            if (!id) return false;
            if (seen.has(id)) return false;

            seen.add(id);

            return true;
          });

          if (allowFeedCache) {
            cacheSet(FEED_CACHE_KEY, deduped);
          }

          return deduped;
        });

        if (userId) {
          setSavedItems((prev) => {
            const next = [
              ...new Set([
                ...prev,
                ...newItems
                  .filter((x) => x?.viewer?.isSaved)
                  .map((x) => getUnifiedId(x)),
              ]),
            ];

            updateInteractionsCachePartial(userId, { savedItems: next });

            return next;
          });

          setLikedItems((prev) => {
            const next = [
              ...new Set([
                ...prev,
                ...newItems
                  .filter((x) => x?.viewer?.isLiked)
                  .map((x) => getUnifiedId(x)),
              ]),
            ];

            updateInteractionsCachePartial(userId, { likedItems: next });

            return next;
          });

          setLikesCount((prev) => {
            const next = { ...prev };

            newItems.forEach((item) => {
              const id = getUnifiedId(item);
              const incoming =
                Number(item?.stats?.likes || item?.likesCount || 0) || 0;

              next[id] = Math.max(next[id] || 0, incoming);
            });

            updateInteractionsCachePartial(userId, { likesCount: next });

            return next;
          });

          setSavedCounts((prev) => {
            const next = { ...prev };

            newItems.forEach((item) => {
              next[getUnifiedId(item)] =
                Number(
                  item?.stats?.saves ||
                    item?.favoritesCount ||
                    item?.savesCount ||
                    0
                ) || 0;
            });

            updateInteractionsCachePartial(userId, { savedCounts: next });

            return next;
          });

          setCommentsCounts((prev) => {
            const next = { ...prev };

            newItems.forEach((item) => {
              next[getUnifiedId(item)] =
                Number(item?.stats?.comments || item?.commentsCount || 0) || 0;
            });

            updateInteractionsCachePartial(userId, { commentsCounts: next });

            return next;
          });
        }
      }

      setFeedCursor(data.nextCursor || null);
      setHasMoreFeed(!!data.hasMore);
    } catch (e) {
      console.error("Feed loadMore error:", e);
    } finally {
      setLoadingMore(false);
    }
  }, [
    loadingMore,
    feedCursor,
    hasMoreFeed,
    categoryName,
    shoppingOnly,
    audience,
    department,
    filter,
    FEED_CACHE_KEY,
    allowFeedCache,
    getRank,
    getUnifiedId,
    updateInteractionsCachePartial,
  ]);

  const { filteredItems, counts } = useMemo(() => {
  const isWantedListing = (i) =>
    i?.cardType === "listing" &&
    String(i?.listingType || i?.typeRaw || "").toLowerCase() === "wanted";

  const ctg = {
    listing: items.filter(
      (i) =>
        i.cardType === "listing" &&
        !isWantedListing(i)
    ),

    wanted: items.filter((i) => isWantedListing(i)),

    post: items.filter((i) => i.type === "post"),
  };

  const allGridItems = items.filter(
    (i) => i?.cardType === "listing" && !isWantedListing(i)
  );

  const filtered =
    filter === "listing"
      ? ctg.listing
      : filter === "wanted"
      ? ctg.wanted
      : filter === "post"
      ? ctg.post
      : allGridItems;

  return {
    filteredItems: filtered,
    counts: {
      all: allGridItems.length,
      listing: ctg.listing.length,
      wanted: ctg.wanted.length,
      post: ctg.post.length,
    },
  };
}, [items, filter]);

const dynamicCollectionRows = useMemo(() => {
  if (filter !== "all") return [];
  if (categoryName) return [];

  const listingPool = items.filter(
    (item) =>
      item?.cardType === "listing" &&
      String(item?.listingType || item?.typeRaw || "").toLowerCase() !==
        "wanted"
  );

  if (!listingPool.length) return [];

  const firstRow = buildDynamicCollections(listingPool, collectionSeed);

  const usedFirstRowIds = new Set();

  firstRow.forEach((collection) => {
    const collectionItems = Array.isArray(collection?.items)
      ? collection.items
      : [];

    collectionItems.forEach((item) => {
      const id = String(item?.listingId || item?.id || "");
      if (id) usedFirstRowIds.add(id);
    });
  });

  const secondPool = listingPool.filter((item) => {
    const id = String(item?.listingId || item?.id || "");
    return id && !usedFirstRowIds.has(id);
  });

  const secondRow = buildDynamicCollections(
    secondPool.length >= 8 ? secondPool : listingPool,
    collectionSeed + 777
  );

  const usedSecondRowIds = new Set();

  secondRow.forEach((collection) => {
    const collectionItems = Array.isArray(collection?.items)
      ? collection.items
      : [];

    collectionItems.forEach((item) => {
      const id = String(item?.listingId || item?.id || "");
      if (id) usedSecondRowIds.add(id);
    });
  });

  const thirdPool = listingPool.filter((item) => {
    const id = String(item?.listingId || item?.id || "");
    return id && !usedFirstRowIds.has(id) && !usedSecondRowIds.has(id);
  });

  const thirdRow = buildDynamicCollections(
    thirdPool.length >= 8 ? thirdPool : listingPool,
    collectionSeed + 1555
  );

  return [
    firstRow,
    secondRow,
    thirdRow,
  ].filter((row) => Array.isArray(row) && row.length > 0);
}, [items, filter, categoryName, collectionSeed]);

  const renderItems = useMemo(() => {
    if (filter !== "all") {
      return spreadSameAuthorListings(filteredItems);
    }

    if (categoryName) {
      return spreadSameAuthorListings(filteredItems);
    }

    const ann = (Array.isArray(announcements) ? announcements : []).map(
      (a, idx) => ({
        ...a,
        type: "showcase_announcement",
        annType: a?.type || "other",
        _annId: a?.id ? String(a.id) : `ann#${idx}`,
        rank: Number(a?.rank || a?.createdAt || 0) || 0,
        createdAt: Number(a?.createdAt || 0) || 0,
      })
    );

    const combined = mixAnnouncementsIntoFeed(filteredItems, ann, 6);

    return spreadSameAuthorListings(combined);
  }, [filteredItems, announcements, filter, categoryName, getRank]);

  const loadMoreRef = useRef(null);

  useEffect(() => {
    if (!hasMoreFeed) return;
    if (loadingMore) return;

    const node = loadMoreRef.current;

    if (!node) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries[0];

        if (!first?.isIntersecting) return;
        if (loadingMore) return;

        loadMore();
      },
      {
        root: null,
        rootMargin: "600px 0px 600px 0px",
        threshold: 0.01,
      }
    );

    obs.observe(node);

    return () => {
      try {
        obs.unobserve(node);
      } catch {}

      obs.disconnect();
    };
  }, [loadMore, hasMoreFeed, loadingMore, renderItems.length]);

  const toggleSave = async (item) => {
    const userId = getCurrentUserId();
    if (!userId) return;

    const postId = getUnifiedId(item);

    try {
      const savedToken = await getAccessToken().catch(() => null);
      if (savedItems.includes(postId)) {
        await axios.delete(`${API_BASE}/api/saved`, {
          data: { userId, postId },
          headers: { Authorization: `Bearer ${savedToken}` },
        });

        setSavedItems((prev) => {
          const next = prev.filter((id) => id !== postId);
          updateInteractionsCachePartial(userId, { savedItems: next });
          return next;
        });

        setSavedCounts((prev) => {
          const next = {
            ...prev,
            [postId]: Math.max(0, (prev?.[postId] || 1) - 1),
          };

          updateInteractionsCachePartial(userId, { savedCounts: next });

          return next;
        });
      } else {
        await axios.post(`${API_BASE}/api/saved`, {
        userId,
        postId,
        type: item.type,
      }, {
        headers: { Authorization: `Bearer ${savedToken}` },
      });

      if (item?.cardType === "listing" || item?.type === "listing") {
        trackSaveListing({
          listingId: postId,
          category: item?.category || null,
        });
      }

      setSavedItems((prev) => {
          const next = [...prev, postId];
          updateInteractionsCachePartial(userId, { savedItems: next });
          return next;
        });

        setSavedCounts((prev) => {
          const next = {
            ...prev,
            [postId]: (prev?.[postId] || 0) + 1,
          };

          updateInteractionsCachePartial(userId, { savedCounts: next });

          return next;
        });
      }
    } catch (err) {
      console.error("Помилка при збереженні:", err);
    }
  };

  const toggleLike = async (item) => {
    const userId = getCurrentUserId();

    if (!userId) return;

    const postId = getUnifiedId(item);
    const type = item?.type === "post" ? "post" : "listing";

    const prevLikedItems = [...likedItems];
    const prevLikesCount = { ...likesCount };
    const wasLiked = likedItems.includes(postId);

    const optimisticLikedItems = wasLiked
      ? likedItems.filter((id) => id !== postId)
      : [...likedItems, postId];

    const optimisticCount = Math.max(
      0,
      (likesCount[postId] || 0) + (wasLiked ? -1 : 1)
    );

    setLikedItems(optimisticLikedItems);

    setLikesCount((prev) => {
      const next = {
        ...prev,
        [postId]: optimisticCount,
      };

      updateInteractionsCachePartial(userId, {
        likedItems: optimisticLikedItems,
        likesCount: next,
      });

      return next;
    });

    try {
      const res = await fetch(`${API_BASE}/api/interactions/like`, {
        method: "POST",
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          id: postId,
          type,
        }),
      });

      if (!res.ok) {
        throw new Error(`toggleLike failed: ${res.status}`);
      }

      const data = await res.json().catch(() => ({}));
      const nextIsLiked = !!data?.viewer?.isLiked;
      const serverLikesCount = Number(data?.counts?.likes);

            if (!wasLiked && nextIsLiked && type === "listing") {
        trackLikeListing({
          listingId: postId,
          category: item?.category || null,
        });
      }

      setLikedItems((prev) => {
        const has = prev.includes(postId);
        let next;

        if (nextIsLiked && !has) next = [...prev, postId];
        else if (!nextIsLiked && has) {
          next = prev.filter((id) => String(id) !== String(postId));
        } else {
          next = prev;
        }

        updateInteractionsCachePartial(userId, { likedItems: next });

        return next;
      });

      setLikesCount((prev) => {
        const current = Number(prev?.[postId] || 0);

        let safeLikesCount = optimisticCount;

        if (Number.isFinite(serverLikesCount)) {
          if (nextIsLiked) {
            safeLikesCount = Math.max(
              current,
              optimisticCount,
              serverLikesCount
            );
          } else {
            safeLikesCount = Math.min(
              current,
              optimisticCount,
              serverLikesCount
            );

            safeLikesCount = Math.max(0, safeLikesCount);
          }
        }

        const next = {
          ...prev,
          [postId]: safeLikesCount,
        };

        updateInteractionsCachePartial(userId, { likesCount: next });

        return next;
      });
    } catch (err) {
      console.error("toggleLike error:", err);

      setLikedItems(prevLikedItems);
      setLikesCount(prevLikesCount);

      updateInteractionsCachePartial(userId, {
        likedItems: prevLikedItems,
        likesCount: prevLikesCount,
      });
    }
  };

  const handleDoubleClick = useCallback(
    (item) => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }

      const postId = getUnifiedId(item);

      if (!likedItems.includes(postId)) toggleLike(item);
    },
    [likedItems, getUnifiedId]
  );

  const prefetchListing = useCallback(async (listingId, mainPhotoUrl) => {
    try {
      if (mainPhotoUrl) {
        const prev = listingCacheRef.current.get(listingId) || {};

        listingCacheRef.current.set(listingId, {
          ...prev,
          thumb: mainPhotoUrl,
        });

        const img = new Image();
        img.decoding = "async";
        img.loading = "eager";
        img.src = mainPhotoUrl;
      }

      if (listingCacheRef.current.get(listingId)?.data) return;

      const res = await fetch(`${API_BASE}/api/listings/${listingId}`);

      if (!res.ok) return;

      const data = await res.json();
      const prev = listingCacheRef.current.get(listingId) || {};

      listingCacheRef.current.set(listingId, {
        ...prev,
        data,
      });
    } catch {}
  }, []);

  const openListingDetailsTracked = useCallback(
  (listingItem) => {
    const listingId = String(
      listingItem?.listingId || listingItem?.id || ""
    );

    if (!listingId) return;

    trackOpenListingDetails({
      listingId,
      category: listingItem?.category || null,
    });

    const thumb =
      listingItem.mainPhoto ||
      (Array.isArray(listingItem.photos) && listingItem.photos[0]) ||
      null;

    openListingModalWithURL(listingId, thumb);
  },
  [openListingModalWithURL]
);

const handleOpenCollection = useCallback((collection) => {
  trackOpenCollection(collection);
  setActiveCollection(collection);
}, []);

  const handleImpressionFactory = (item, postId) => async () => {
    return;

    try {
      const userId = getCurrentUserId();
      if (!userId) return;

      const ssKey = `impr:${item.type}:${postId}`;

      if (sessionStorage.getItem(ssKey)) return;

      const headers = await getAuthHeaders();

      if (item.cardType === "listing") {
        const payload = item?.createdAt ? { createdAt: item.createdAt } : {};

        await axios.post(
          `${API_BASE}/api/listings/${postId}/view-unique`,
          payload,
          { headers }
        );
      } else if (item.type === "post") {
        const payload = item?.createdAt ? { createdAt: item.createdAt } : {};

        await axios.post(
          `${API_BASE}/api/posts/${postId}/view-unique`,
          payload,
          { headers }
        );
      }

      sessionStorage.setItem(ssKey, "1");

      if (item.cardType === "listing" && ENABLE_LISTING_PREFETCH) {
        const mainPhoto =
          item.mainPhoto ||
          (Array.isArray(item.photos) && item.photos[0]) ||
          null;

        prefetchListing(String(item.listingId), mainPhoto);
      }
    } catch {}
  };

  const onSelectFilter = (next) => {
    setFilter(next);
    localStorage.setItem(FEED_FILTER_LS_KEY, next);

    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {}
  };

  const handleListingDeleted = useCallback(
    (deletedListingId) => {
      const sId = String(deletedListingId);

      deletedIdsRef.current.add(sId);

      setItems((prev) => {
        const next = prev.filter((x) => getUnifiedId(x) !== sId);

        if (allowFeedCache) {
          cacheSet(FEED_CACHE_KEY, next);
        }

        return next;
      });

      setRemovingIds((prev) => prev.filter((x) => x !== sId));

      setSavedItems((prev) => {
        const next = prev.filter((id) => String(id) !== sId);
        const userId = getCurrentUserId();

        if (userId) updateInteractionsCachePartial(userId, { savedItems: next });

        return next;
      });

      setLikedItems((prev) => {
        const next = prev.filter((id) => String(id) !== sId);
        const userId = getCurrentUserId();

        if (userId) updateInteractionsCachePartial(userId, { likedItems: next });

        return next;
      });

      setLikesCount((prev) => {
        const copy = { ...(prev || {}) };
        delete copy[sId];

        const userId = getCurrentUserId();

        if (userId) updateInteractionsCachePartial(userId, { likesCount: copy });

        return copy;
      });

      setSavedCounts((prev) => {
        const copy = { ...(prev || {}) };
        delete copy[sId];

        const userId = getCurrentUserId();

        if (userId) updateInteractionsCachePartial(userId, { savedCounts: copy });

        return copy;
      });

      setCommentsCounts((prev) => {
        const copy = { ...(prev || {}) };
        delete copy[sId];

        const userId = getCurrentUserId();

        if (userId) {
          updateInteractionsCachePartial(userId, {
            commentsCounts: copy,
          });
        }

        return copy;
      });

      try {
        listingCacheRef.current.delete(sId);
      } catch {}

      closeListingModalAndURL();
    },
    [
      FEED_CACHE_KEY,
      allowFeedCache,
      getUnifiedId,
      closeListingModalAndURL,
      updateInteractionsCachePartial,
    ]
  );

  const BASE_DELAY = 20;
  const STEP = 26;

  if (loading) {
    return <FeedSkeleton />;
  }

  const sliceEnd = renderItems.length;

  const getCompletedRowIndex = (desiredCount) => {
  const cols = Math.max(1, Number(gridColumns || 2));
    return Math.max(Math.ceil(desiredCount / cols) * cols - 1, 0);
  };

const showcaseInsertAfter = getCompletedRowIndex(8);

const collectionInsertAfter = getCompletedRowIndex(20);

const wantedInsertAfter = getCompletedRowIndex(28);
const postInsertAfter = getCompletedRowIndex(40);

const secondCollectionInsertAfter = getCompletedRowIndex(60);
const thirdCollectionInsertAfter = getCompletedRowIndex(84);

const adInsertAfter = getCompletedRowIndex(104);
  return (
    <>
      {categoryName && (
        <div
          className="feed__category-pill tg-appear"
          style={{ ["--stagger"]: `8ms` }}
        >
          {categoryImages?.[categoryName] && (
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                backgroundImage: `url(${categoryImages[categoryName]})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                boxShadow: "inset 0 0 0 1px rgba(0,0,0,.06)",
              }}
            />
          )}

          <span>Категорія:</span>
          <b>{categoryName}</b>
        </div>
      )}

      <div
        className="feed-topbar tg-appear"
        role="navigation"
        aria-label="Фільтр публікацій"
        style={{ ["--stagger"]: `0ms` }}
      >
        <h2
          className="feed-topbar__title tg-appear"
          style={{ ["--stagger"]: `12ms` }}
        >
          Недавні публікації
        </h2>

        <div
          className="feed-tabs tg-appear"
          role="tablist"
          aria-label="Тип публікацій"
          style={{
            ["--activeIndex"]:
              filter === "all"
                ? 0
                : filter === "listing"
                ? 1
                : filter === "wanted"
                ? 2
                : 3,
          }}
        >
          <button
            role="tab"
            aria-selected={filter === "all"}
            className={`feed-tab ${
              filter === "all" ? "active" : ""
            } tg-appear`}
            style={{ ["--stagger"]: `24ms` }}
            onClick={() => onSelectFilter("all")}
          >
            Всі
          </button>

          <button
            role="tab"
            aria-selected={filter === "listing"}
            className={`feed-tab ${
              filter === "listing" ? "active" : ""
            } tg-appear`}
            style={{ ["--stagger"]: `36ms` }}
            onClick={() => onSelectFilter("listing")}
          >
            Оголошення
          </button>

          <button
            role="tab"
            aria-selected={filter === "wanted"}
            className={`feed-tab ${
              filter === "wanted" ? "active" : ""
            } tg-appear`}
            style={{ ["--stagger"]: `48ms` }}
            onClick={() => onSelectFilter("wanted")}
          >
            Шукаю
          </button>

          <button
            role="tab"
            aria-selected={filter === "post"}
            className={`feed-tab ${
              filter === "post" ? "active" : ""
            } tg-appear`}
            style={{ ["--stagger"]: `60ms` }}
            onClick={() => onSelectFilter("post")}
          >
            Пости
          </button>
        </div>
      </div>

      {!categoryName && filter === "all" && <FeedbackBanner />}

      {/* Telegram promo temporarily disabled — keep component/import so it
          can be turned back on easily. */}
      {/* {!categoryName && filter === "all" && <TelegramBaner />} */}

      <div className="feed">
        {renderItems.length === 0 ? (
          <p
            className="feed__empty tg-appear"
            style={{ ["--stagger"]: `60ms` }}
          >
            Поки що немає контенту
          </p>
        ) : (
          renderItems.slice(0, sliceEnd).map((item, i) => {
            const postId =
              item.type === "showcase_announcement"
                ? item._annId
                  ? String(item._annId)
                  : `ann#${i}`
                : getUnifiedId(item);

            if (!postId || deletedIdsRef.current.has(postId)) {
              return null;
            }

            const isSaved =
              item.type === "showcase_announcement"
                ? false
                : savedItems.includes(postId);

            const isLiked =
              item.type === "showcase_announcement"
                ? false
                : likedItems.includes(postId);

            const timeAgo = formatTimeAgo(item.createdAt);
            const delay = BASE_DELAY + i * STEP;
            const isRemoving = removingIds.includes(postId);

            return (
              <React.Fragment key={postId}>
                <ImpressionOnce
                  threshold={0.6}
                  onSeen={
                    item.type === "showcase_announcement"
                      ? null
                      : handleImpressionFactory(item, postId)
                  }
                  className={`feed__grid-item ${
                    item.cardType === "listing"
                      ? "feed__grid-item--listing"
                      : "feed__grid-item--wide"
                  }`}
                  style={{ ["--stagger"]: `${delay}ms` }}
                >
                  <div
                    className="tg-appear"
                    style={{ ["--stagger"]: `${delay}ms` }}
                  >
                    <div
                      className={`feed__card tg-appear ${
                        item.cardType === "listing"
                          ? "feed__card--listing"
                          : "feed__card--wide"
                      } ${isRemoving ? "feed__card--leaving" : ""}`}
                      style={{ ["--stagger"]: `${delay + 8}ms` }}
                    >
                      {item.cardType === "listing" ? (
                        <ListingCard
                          item={item}
                          isSaved={isSaved}
                          isLiked={isLiked}
                          likesCount={likesCount[postId] || 0}
                          commentCount={
                            commentsCounts[postId] ??
                            item?.commentsCount ??
                            item?.stats?.comments ??
                            0
                          }
                          disableInitialTruthFetch={false}
                          onToggleLike={() => toggleLike(item)}
                          onToggleSave={() => toggleSave(item)}
                          onSyncCounts={(patch) => {
                            const pid = String(
                              patch?.listingId ||
                                patch?.postId ||
                                patch?.id ||
                                ""
                            );

                            if (!pid) return;

                            if (typeof patch.likesCount === "number") {
                              setLikesCount((prev) => ({
                                ...prev,
                                [pid]: patch.likesCount,
                              }));
                            }

                            if (typeof patch.favoritesCount === "number") {
                              setSavedCounts((prev) => ({
                                ...prev,
                                [pid]: patch.favoritesCount,
                              }));
                            }

                            if (typeof patch.commentsCount === "number") {
                              setCommentsCounts((prev) => ({
                                ...prev,
                                [pid]: patch.commentsCount,
                              }));
                            }
                          }}
                          onOpenActions={() =>
                            setSelectedItem({
                              id: postId,
                              type: item.type,
                              createdAt: item.createdAt,
                              userId: item.userId,
                              category: item.category || null,
                              authorName:
                                item.authorName ||
                                item.username ||
                                "Користувач",
                              postText:
                                item.text ||
                                item.title ||
                                item.description ||
                                "",
                              postUrl: `${window.location.origin}/listing/${item.listingId}`,
                              raisedUntil: item.boostEndsAtSec
                                ? new Date(
                                    item.boostEndsAtSec * 1000
                                  ).toISOString()
                                : null,
                              lastRaisedAt: item.lastRaisedAt || null,
                            })
                          }
                          onOpenListingDetails={() => openListingDetailsTracked(item)}
                          onDoubleLike={(it) => {
                            if (!likedItems.includes(postId)) {
                              handleDoubleClick(it);
                            }
                          }}
                          navigate={navigate}
                          timeAgo={timeAgo}
                        />
                      ) : item.type === "showcase_announcement" ? (
                        <ShowcaseAnnouncementCard
                          item={item}
                          timeAgo={timeAgo}
                          onOpen={(showcaseId) =>
                            navigate(`/showcase/${showcaseId}`)
                          }
                        />
                      ) : (
                        <PostCard
                          item={item}
                          isSaved={isSaved}
                          isLiked={isLiked}
                          likesCount={likesCount[postId] || 0}
                          disableInitialTruthFetch={true}
                          onToggleLike={() => toggleLike(item)}
                          onToggleSave={() => toggleSave(item)}
                          onSyncCounts={(patch) => {
                            const pid = patch?.postId;

                            if (!pid) return;

                            if (typeof patch.likesCount === "number") {
                              setLikesCount((prev) => ({
                                ...prev,
                                [pid]: patch.likesCount,
                              }));
                            }

                            if (typeof patch.savesCount === "number") {
                              setSavedCounts((prev) => ({
                                ...prev,
                                [pid]: patch.savesCount,
                              }));
                            }

                            if (typeof patch.commentsCount === "number") {
                              setCommentsCounts((prev) => ({
                                ...prev,
                                [pid]: patch.commentsCount,
                              }));
                            }
                          }}
                          onOpenActions={() =>
                            setSelectedItem({
                              id: postId,
                              type: item.type,
                              createdAt: item.createdAt,
                              userId: item.userId,
                              category: item.category || null,
                              authorName:
                                item.authorName ||
                                item.username ||
                                "Користувач",
                              postText: item.text || "",
                              postUrl: `${window.location.origin}/post/${item.postId}`,
                            })
                          }
                          navigate={navigate}
                        />
                      )}
                    </div>
                  </div>
                </ImpressionOnce>

                                                {i === showcaseInsertAfter &&
                  showShowcaseRecs &&
                  filter === "all" &&
                  !categoryName && (
                    <div
                      className="feed__showcase-recs tg-appear"
                      style={{ ["--stagger"]: `${delay + 10}ms` }}
                    >
                      <ShowcasesRecommendRow
                        items={showcaseRecs}
                        onOpen={(showcaseId) => navigate(`/showcase/${showcaseId}`)}
                      />
                    </div>
                  )}

                  {i === collectionInsertAfter &&
                  dynamicCollectionRows[0]?.length > 0 &&
                  filter === "all" &&
                  !categoryName && (
                    <div
                      className="feed__collections-recs tg-appear"
                      style={{ ["--stagger"]: `${delay + 10}ms` }}
                    >
                      <CollectionsRecommendRow
                        items={dynamicCollectionRows[0]}
                        onOpen={handleOpenCollection}
                      />
                    </div>
                  )}

                {i === secondCollectionInsertAfter &&
                  dynamicCollectionRows[1]?.length > 0 &&
                  filter === "all" &&
                  !categoryName && (
                    <div
                      className="feed__collections-recs tg-appear"
                      style={{ ["--stagger"]: `${delay + 10}ms` }}
                    >
                      <CollectionsRecommendRow
                        items={dynamicCollectionRows[1]}
                        onOpen={handleOpenCollection}
                      />
                    </div>
                  )}

                {i === thirdCollectionInsertAfter &&
                  dynamicCollectionRows[2]?.length > 0 &&
                  filter === "all" &&
                  !categoryName && (
                    <div
                      className="feed__collections-recs tg-appear"
                      style={{ ["--stagger"]: `${delay + 10}ms` }}
                    >
                      <CollectionsRecommendRow
                        items={dynamicCollectionRows[2]}
                        onOpen={handleOpenCollection}                      />
                    </div>
                  )}

                  {i === wantedInsertAfter &&
                    showWantedRecs &&
                    filter === "all" &&
                    !categoryName && (
                      <div
                        className="feed__wanted-recs tg-appear"
                        style={{ ["--stagger"]: `${delay + 10}ms` }}
                      >
                        <WantedRecommendRow
                          items={wantedRecs}
                          onOpenListing={(wantedItem) => openListingDetailsTracked(wantedItem)}
                          onMore={() => onSelectFilter("wanted")}
                        />
                      </div>
                    )}

                    {i === postInsertAfter &&
                      showPostRecs &&
                      filter === "all" &&
                      !categoryName && (
                        <div
                          className="feed__post-recs tg-appear"
                          style={{ ["--stagger"]: `${delay + 10}ms` }}
                        >
                          <PostsRecommendRow
                            items={postRecs}
                            onOpenPost={() => onSelectFilter("post")}
                            onMore={() => onSelectFilter("post")}
                          />
                        </div>
                      )}

                {canSeeAds && i === adInsertAfter && (
                  <div className="feed__wide">
                    <AdSlot placement="feed_inline_1" />
                  </div>
                )}
              </React.Fragment>
            );
          })
        )}

        {renderItems.length > 0 && hasMoreFeed && (
          <div
            ref={loadMoreRef}
            className="feed__sentinel"
            aria-hidden="true"
          />
        )}

        {renderItems.length > 0 && loadingMore && (
          <div className="feed__loading-more">Завантажуємо ще...</div>
        )}

        {renderItems.length > 0 && !hasMoreFeed && !loadingMore && (
          <div className="feed__end">На цьому поки все☺️</div>
        )}

        {selectedItem && (
          <FeedActions
            type={selectedItem.type}
            id={selectedItem.id}
            createdAt={selectedItem.createdAt}
            isOwner={selectedItem.userId === getCurrentUserId()}
            onClose={() => setSelectedItem(null)}
            onDelete={handleDeleteFromFeed}
            authorId={selectedItem.userId}
            authorName={selectedItem.authorName}
            postText={selectedItem.postText}
            postUrl={selectedItem.postUrl}
            category={selectedItem.category}
            onHideCard={handleHideCard}
            raisedUntil={selectedItem.raisedUntil}
            lastRaisedAt={selectedItem.lastRaisedAt}
          />
        )}

        {modalListingId && (
          <ListingDetails
            listingId={modalListingId}
            isModal={true}
            onClose={closeListingModalAndURL}
            initialListing={
              listingCacheRef.current.get(modalListingId)?.data || null
            }
            initialThumb={
              listingCacheRef.current.get(modalListingId)?.thumb || null
            }
            onDeleted={handleListingDeleted}
          />
        )}

        {activeCollection && (
        <CollectionModal
          open={!!activeCollection}
          collection={activeCollection}
          onClose={() => setActiveCollection(null)}
          navigate={navigate}
          likedItems={likedItems}
          savedItems={savedItems}
          likesCount={likesCount}
          commentsCounts={commentsCounts}
          onToggleLike={toggleLike}
          onToggleSave={toggleSave}
          onOpenListingDetails={(item) => {
          setActiveCollection(null);
          openListingDetailsTracked(item);
        }}
        />
      )}
      </div>
    </>
  );
};

export default Feed;